/* Horizontal ticker (sections/horizontal-ticker.liquid).

   <horizontal-ticker>: keeps the sliding track at least twice as wide as the section by
   repeating the block group (always an even number of copies, so the CSS keyframe's
   translateX(-50%) equals a whole number of groups and the loop is seamless), and turns the
   "Ticker speed" setting into an animation duration from the measured width, so the content
   moves at the same pace whatever is in it. Re-measures when the section or the group changes
   size (fonts, images, editor edits). Optional pause on touch and, in the theme editor, while
   a block is selected.

   <ticker-video>: the video item's play/pause, mute/unmute and timeline controls. */
(() => {
  const define = (name, cls) => {
    if (window.Multiverse && typeof window.Multiverse.defineOnce === 'function') {
      window.Multiverse.defineOnce(name, cls);
    } else if (!customElements.get(name)) {
      customElements.define(name, cls);
    }
  };

  // Pixels per second at speed 1; the range setting (1–5, step 0.5) multiplies it.
  const PX_PER_SECOND = 40;
  const GROUP = '.mv-ticker__group';

  class HorizontalTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mv-ticker__track');
      this.base = this.track ? this.track.querySelector(GROUP) : null;
      if (!this.track || !this.base) return;

      this.speed = parseFloat(this.dataset.speed) || 3;
      this.onResize = () => this.schedule();
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this);
      this.observer.observe(this.base);
      this.schedule();

      if (this.dataset.pauseOnTouch === 'true') this.bindTouchPause();
      if (window.Shopify && window.Shopify.designMode) this.bindEditor();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.cancelAnimationFrame(this.frame);
      if (this.unbindEditor) this.unbindEditor();
    }

    schedule() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.fill());
    }

    // The template copy (aria-hidden, no editor attributes) is preferred as the clone source;
    // the first group is only used when it is alone (and then stripped of editor attributes).
    cloneSource() {
      const groups = this.track.querySelectorAll(GROUP);
      return groups.length > 1 ? groups[1] : groups[0];
    }

    makeClone() {
      const clone = this.cloneSource().cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    }

    fill() {
      const groupWidth = this.base.getBoundingClientRect().width;
      const viewport = this.clientWidth;
      if (!groupWidth || !viewport) return;

      // Enough copies to cover the section twice, rounded up to an even count.
      let copies = Math.ceil((viewport * 2) / groupWidth);
      copies = Math.max(2, Math.ceil(copies / 2) * 2);

      let groups = this.track.querySelectorAll(GROUP);
      while (groups.length < copies) {
        this.track.appendChild(this.makeClone());
        groups = this.track.querySelectorAll(GROUP);
      }
      while (groups.length > copies && groups.length > 2) {
        groups[groups.length - 1].remove();
        groups = this.track.querySelectorAll(GROUP);
      }

      // One cycle moves half the track.
      const distance = (groupWidth * copies) / 2;
      const seconds = distance / (this.speed * PX_PER_SECOND);
      this.style.setProperty('--mv-ticker-duration', `${seconds.toFixed(2)}s`);
      this.classList.add('mv-ticker--ready');
    }

    bindTouchPause() {
      const pause = () => this.classList.add('is-paused');
      const resume = () => this.classList.remove('is-paused');
      this.addEventListener('touchstart', pause, { passive: true });
      this.addEventListener('touchend', resume, { passive: true });
      this.addEventListener('touchcancel', resume, { passive: true });
    }

    // Theme editor: hold the ticker still while one of its blocks is selected so the merchant
    // can see what they are editing.
    bindEditor() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const onSelect = (event) => {
        if (section.contains(event.target)) this.classList.add('is-paused');
      };
      const onDeselect = (event) => {
        if (section.contains(event.target)) this.classList.remove('is-paused');
      };
      document.addEventListener('shopify:block:select', onSelect);
      document.addEventListener('shopify:block:deselect', onDeselect);
      this.unbindEditor = () => {
        document.removeEventListener('shopify:block:select', onSelect);
        document.removeEventListener('shopify:block:deselect', onDeselect);
      };
    }
  }

  class TickerVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playButton = this.querySelector('.mv-ticker-video__play');
      this.soundButton = this.querySelector('.mv-ticker-video__sound');
      this.timeline = this.querySelector('.mv-ticker-video__timeline');

      this.video.addEventListener('play', () => this.syncPlaying(true));
      this.video.addEventListener('pause', () => this.syncPlaying(false));
      this.video.addEventListener('volumechange', () => this.syncMuted());
      if (this.timeline) {
        this.video.addEventListener('timeupdate', () => this.syncProgress());
      }

      if (this.playButton) {
        this.playButton.addEventListener('click', () => {
          if (this.video.paused) {
            const attempt = this.video.play();
            if (attempt && attempt.catch) attempt.catch(() => {});
          } else {
            this.video.pause();
          }
        });
      }

      if (this.soundButton) {
        this.soundButton.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
          this.syncMuted();
        });
      }

      this.syncPlaying(!this.video.paused);
      this.syncMuted();
    }

    syncPlaying(playing) {
      this.classList.toggle('is-playing', playing);
      if (this.playButton) {
        const label = playing ? this.playButton.dataset.labelPause : this.playButton.dataset.labelPlay;
        if (label) this.playButton.setAttribute('aria-label', label);
      }
    }

    syncMuted() {
      const muted = this.video.muted;
      this.classList.toggle('is-muted', muted);
      if (this.soundButton) {
        const label = muted ? this.soundButton.dataset.labelUnmute : this.soundButton.dataset.labelMute;
        if (label) this.soundButton.setAttribute('aria-label', label);
        this.soundButton.setAttribute('aria-pressed', muted ? 'false' : 'true');
      }
    }

    syncProgress() {
      const { currentTime, duration } = this.video;
      const fraction = duration ? currentTime / duration : 0;
      this.style.setProperty('--mv-tv-progress', fraction.toFixed(4));
    }
  }

  define('horizontal-ticker', HorizontalTicker);
  define('ticker-video', TickerVideo);
})();
